import { useCallback, useEffect, useRef } from 'react'
import { Platform } from 'react-native'

import Alert from '@/components/Alert'

type Options = {
  onSave?: () => void
}

export default function usePreventLeaveWeb(isDirty: boolean, message: string, options?: Options) {
  const isPushedRef = useRef(false)
  const isShowingAlertRef = useRef(false)

  const pushGuard = useCallback(() => {
    isPushedRef.current = true
    window.history.pushState({ preventBack: true }, '')
  }, [])

  const removeGuard = useCallback(() => {
    if (isPushedRef.current) {
      isPushedRef.current = false
      window.history.back()
    }
  }, [])

  useEffect(() => {
    if (Platform.OS !== 'web') return

    if (isDirty) {
      pushGuard()

      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault()
      }

      const handlePopState = () => {
        if (isShowingAlertRef.current) return
        isPushedRef.current = false
        isShowingAlertRef.current = true

        const leave = () => {
          isShowingAlertRef.current = false
          window.removeEventListener('popstate', handlePopState)
          window.history.back()
        }

        const buttons = options?.onSave
          ? [
              {
                text: '取消',
                style: 'cancel' as const,
                onPress: () => {
                  isShowingAlertRef.current = false
                  pushGuard()
                },
              },
              {
                text: '不保存',
                style: 'destructive' as const,
                onPress: leave,
              },
              {
                text: '保存',
                isPreferred: true,
                onPress: () => {
                  isShowingAlertRef.current = false
                  pushGuard()
                  options.onSave!()
                },
              },
            ]
          : [
              {
                text: '取消',
                style: 'cancel' as const,
                onPress: () => {
                  isShowingAlertRef.current = false
                  pushGuard()
                },
              },
              {
                text: '确定',
                style: 'destructive' as const,
                onPress: leave,
              },
            ]

        Alert.alert(message, '当前编辑内容尚未保存，是否在离开前保存？', buttons)
      }

      window.addEventListener('beforeunload', handleBeforeUnload)
      window.addEventListener('popstate', handlePopState)
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload)
        window.removeEventListener('popstate', handlePopState)
      }
    } else {
      removeGuard()
    }
  }, [isDirty, message, pushGuard, removeGuard, options])
}
